import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { marketplaceListingReportService, MarketplaceListingReport } from '../services/api';
import { Link } from 'react-router-dom';
import CloseListingModal from '../components/CloseListingModal';

const AdminListingReportsPage = () => {
    const { t } = useTranslation();
    const [reports, setReports] = useState<MarketplaceListingReport[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('PENDING');
    const [actionLoading, setActionLoading] = useState<number | null>(null);

    // Modal State
    const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState<MarketplaceListingReport | null>(null);

    useEffect(() => {
        fetchReports();
    }, [statusFilter]);

    const fetchReports = async () => {
        setLoading(true);
        try {
            const response = await marketplaceListingReportService.getReports({ status: statusFilter, limit: 100 });
            setReports(response.data.data);
        } catch (error) {
            console.error('Failed to fetch reports:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleResolveClick = (report: MarketplaceListingReport) => {
        setSelectedReport(report);
        setIsCloseModalOpen(true);
    };

    const handleConfirmResolve = async (price: number | null, _quantity?: number) => {
        if (!selectedReport) return;

        const reportId = selectedReport.id;
        const closedPrice = price ?? undefined;

        setIsCloseModalOpen(false); // Close modal
        setActionLoading(reportId);

        try {
            await marketplaceListingReportService.resolveReport(reportId, { closedPrice });
            // Remove from list or refresh
            setReports(prev => prev.filter(r => r.id !== reportId));
            alert(t('adminListingReports.actions.resolveSuccess'));
        } catch (error) {
            console.error('Failed to resolve report:', error);
            alert(t('adminListingReports.actions.resolveError'));
        } finally {
            setActionLoading(null);
            setSelectedReport(null);
        }
    };

    const handleReject = async (reportId: number) => {
        if (!window.confirm(t('adminListingReports.actions.confirmReject'))) return;

        setActionLoading(reportId);
        try {
            await marketplaceListingReportService.rejectReport(reportId);
            setReports(prev => prev.filter(r => r.id !== reportId));
        } catch (error) {
            console.error('Failed to reject report:', error);
            alert(t('adminListingReports.actions.rejectError'));
        } finally {
            setActionLoading(null);
        }
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">{t('adminListingReports.title')}</h1>
                <div className="flex gap-2">
                    <button
                        onClick={() => setStatusFilter('PENDING')}
                        className={`px-4 py-2 rounded-lg ${statusFilter === 'PENDING' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        {t('adminListingReports.pending')}
                    </button>
                    <button
                        onClick={() => setStatusFilter('RESOLVED')}
                        className={`px-4 py-2 rounded-lg ${statusFilter === 'RESOLVED' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        {t('adminListingReports.resolved')}
                    </button>
                    <button
                        onClick={() => setStatusFilter('REJECTED')}
                        className={`px-4 py-2 rounded-lg ${statusFilter === 'REJECTED' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        {t('adminListingReports.rejected')}
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-8">{t('common.loading')}</div>
            ) : reports.length === 0 ? (
                <div className="bg-gray-50 p-8 rounded-lg text-center text-gray-500">
                    {t('adminListingReports.noReports')}
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('adminListingReports.table.reportInfo')}</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('adminListingReports.table.listing')}</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('adminListingReports.table.reason')}</th>
                                {statusFilter === 'PENDING' && (
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('adminListingReports.table.actions')}</th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {reports.map((report) => (
                                <tr key={report.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{t('adminListingReports.table.id')} {report.id}</div>
                                        <div className="text-sm text-gray-500">
                                            {t('adminListingReports.table.from')} {report.reporter?.displayName || t('adminListingReports.table.unknown')}
                                        </div>
                                        <div className="text-xs text-gray-400">
                                            {new Date(report.createdAt).toLocaleString('th-TH')}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {report.listing ? (
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    <Link to={`/marketplace/listing/${report.listingId}`} className="text-blue-600 hover:underline" target="_blank">
                                                        {t('adminListingReports.table.viewListing', { id: report.listingId })}
                                                    </Link>
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {t('adminListingReports.table.seller')} {report.listing.seller?.displayName}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {t('adminListingReports.table.listingType')} {report.listing.type} • {t('adminListingReports.table.price')} {report.listing.price?.toLocaleString()}
                                                </div>
                                                {report.listing.card && (
                                                    <div className="flex items-center mt-1">
                                                        <img src={report.listing.card.imageUrl} alt="" className="w-8 h-8 rounded mr-2 object-cover" />
                                                        <span className="text-xs">{report.listing.card.name}</span>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-red-500">{t('adminListingReports.table.listingDeleted')}</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-900 max-w-md whitespace-normal break-words">
                                            {report.reason || t('adminListingReports.table.noReason')}
                                        </div>
                                    </td>
                                    {statusFilter === 'PENDING' && (
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => handleResolveClick(report)}
                                                disabled={actionLoading === report.id}
                                                className="text-red-600 hover:text-red-900 mr-4 disabled:opacity-50"
                                            >
                                                {actionLoading === report.id ? t('adminListingReports.actions.processing') : t('adminListingReports.actions.resolve')}
                                            </button>
                                            <button
                                                onClick={() => handleReject(report.id)}
                                                disabled={actionLoading === report.id}
                                                className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                                            >
                                                {t('adminListingReports.actions.ignore')}
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {/* Close Listing Modal */}
            <CloseListingModal
                isOpen={isCloseModalOpen}
                onClose={() => setIsCloseModalOpen(false)}
                onConfirm={handleConfirmResolve}
                listingType={(selectedReport?.listing?.type as 'WTS' | 'WTB') || 'WTS'}
                maxQuantity={selectedReport?.listing?.quantity || 1}
                defaultPrice={selectedReport?.listing?.price}
            />
        </div>
    );
};

export default AdminListingReportsPage;
